import React, { createContext, useContext, useEffect, ReactNode } from "react";
import { initGA, trackEvent, EventNames, UserProperties } from "./analytics";
import { initGlobalErrorTracking } from "./errorTracking";
import {
  useScrollDepthTracking,
  useTimeOnPage,
  ExitIntentTracker,
} from "./advancedTracking";

// Create a context for analytics
interface AnalyticsContextType {
  trackPageView: (
    url: string,
    pageTitle?: string,
    pageTemplate?: string
  ) => void;
  trackEvent: (eventName: string, eventParams?: Record<string, any>) => void;
  trackConversion: (
    eventName: string,
    eventParams?: Record<string, any>
  ) => void;
  setUserProperties: (properties: Record<string, any>) => void;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(
  undefined
);

// Provider component
interface AnalyticsProviderProps {
  children: ReactNode;
  trackScrollDepth?: boolean;
  trackTimeOnPage?: boolean;
  trackExitIntent?: boolean;
  trackPerformance?: boolean;
}

export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  children,
  trackScrollDepth = true,
  trackTimeOnPage = true,
  trackExitIntent = true,
  trackPerformance = true,
}) => {
  // Initialize analytics on mount
  useEffect(() => {
    // Initialize Google Analytics
    initGA();

    // Initialize global error tracking
    initGlobalErrorTracking();

    // Track initial page load
    trackEvent(EventNames.PAGE_VIEW, {
      page_path: window.location.pathname,
      page_title: document.title,
      referrer: document.referrer || "direct",
      initial_load: true,
    });

    // Track browser information
    trackEvent("browser_info", {
      user_agent: navigator.userAgent,
      language: navigator.language,
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      pixel_ratio: window.devicePixelRatio,
      connection_type: (navigator as any).connection
        ? (navigator as any).connection.effectiveType
        : "unknown",
    });

    // Detect if user is returning or new
    const hasVisitedBefore = localStorage.getItem("eon_visited");
    const isReturningUser = !!hasVisitedBefore;

    // Track user type
    trackEvent("user_visit_type", {
      user_type: isReturningUser ? "returning" : "new",
      visit_count: isReturningUser ? parseInt(hasVisitedBefore || "0") + 1 : 1,
    });

    // Update visit count
    localStorage.setItem(
      "eon_visited",
      isReturningUser ? (parseInt(hasVisitedBefore || "0") + 1).toString() : "1"
    );

    // Track when the user leaves
    window.addEventListener("beforeunload", () => {
      trackEvent("page_exit", {
        page_path: window.location.pathname,
        page_title: document.title,
        exit_time: new Date().toISOString(),
      });
    });

    // Clean up
    return () => {
      window.removeEventListener("beforeunload", () => {});
    };
  }, []);

  // Always call hooks, but conditionally use their functionality
  useScrollDepthTracking(trackScrollDepth ? [25, 50, 75, 90, 100] : []); // Empty array means no tracking

  // Always call the time on page hook but conditionally track based on prop
  useTimeOnPage({
    milestones: trackTimeOnPage ? [10, 30, 60, 120, 300] : [],
    includeOnUnload: trackTimeOnPage,
  });

  // Initialize performance tracking
  useEffect(() => {
    if (trackPerformance && "PerformanceObserver" in window) {
      try {
        // Track Core Web Vitals and other performance metrics
        const perfObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            if (entry.entryType === "largest-contentful-paint") {
              trackEvent("core_web_vitals", {
                metric: "LCP",
                value: entry.startTime,
                rating:
                  entry.startTime < 2500
                    ? "good"
                    : entry.startTime < 4000
                    ? "needs-improvement"
                    : "poor",
              });
            }

            if (entry.entryType === "first-input") {
              const fid =
                (entry as any).processingStart - (entry as any).startTime;
              trackEvent("core_web_vitals", {
                metric: "FID",
                value: fid,
                rating:
                  fid < 100 ? "good" : fid < 300 ? "needs-improvement" : "poor",
              });
            }

            if (
              entry.entryType === "layout-shift" &&
              !(entry as any).hadRecentInput
            ) {
              const cls = (entry as any).value;
              // CLS is cumulative, so this tracks individual shifts
              trackEvent("layout_shift", {
                value: cls,
              });
            }
          });
        });

        perfObserver.observe({
          type: "largest-contentful-paint",
          buffered: true,
        });
        perfObserver.observe({ type: "first-input", buffered: true });
        perfObserver.observe({ type: "layout-shift", buffered: true });

        return () => {
          perfObserver.disconnect();
        };
      } catch (e) {
        console.error("Performance Observer error:", e);
      }
    }
  }, [trackPerformance]);

  // Expose analytics methods to consumers
  const contextValue: AnalyticsContextType = {
    trackPageView: (url, pageTitle, pageTemplate) => {
      import("./analytics").then(({ trackPageView }) => {
        trackPageView(url, pageTitle, pageTemplate);
      });
    },
    trackEvent: (eventName, eventParams) => {
      import("./analytics").then(({ trackEvent }) => {
        trackEvent(eventName, eventParams);
      });
    },
    trackConversion: (eventName, eventParams) => {
      import("./analytics").then(({ trackConversion }) => {
        trackConversion(eventName, eventParams);
      });
    },
    setUserProperties: (properties) => {
      import("./analytics").then(({ setUserProperties }) => {
        setUserProperties(properties);
      });
    },
  };

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
      {trackExitIntent && <ExitIntentTracker />}
    </AnalyticsContext.Provider>
  );
};

// Custom hook for components to access analytics
export const useAnalytics = (): AnalyticsContextType => {
  const context = useContext(AnalyticsContext);
  if (context === undefined) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider");
  }
  return context;
};
